// components/views/sections/DomainKPIsSection.js - VERSION OPTIMISÉE
import { Brain, Grid, BarChart3, User } from 'lucide-react';
import { useMemo, useState } from 'react';
import { DomainBarChart } from './DomainBarChart';
import { getDomainInfo, getReactIcon } from '../../hooks_suggestion/useDomainSuggestions';

export const DomainKPIsSection = ({ 
    domainesData = [], 
    skillsData = [], 
    refreshTrigger // Trigger pour forcer le recalcul si nécessaire
}) => {
    // State pour gérer la vue actuelle
    const [viewMode, setViewMode] = useState('heatmap'); // 'heatmap' ou 'barchart'

    // REMPLACÉ : Au lieu d'utiliser les hooks, on utilise les données passées en props
    // const { domaines } = useDomaines(); ❌
    // const { skills, getSkillsByDomain } = useSkills(); ❌

    // Transformer les domaines de la DB au format attendu - UTILISE LES PROPS
    const allDomains = useMemo(() => {
        return domainesData.map(domaine => ({
            id: domaine.id_domaine,
            name: domaine.nom_domaine,
            icon: domaine.icon || '👤',
            description: domaine.description,
            score: domaine.score_evaluation || 0,
            level: domaine.niveau_evaluation || 'Débutant',
            type_domaine: domaine.type_domaine,
            isCustom: domaine.type_domaine === 'custom'
        }));
    }, [domainesData, refreshTrigger]); // Ajouter refreshTrigger pour forcer le recalcul

    // Organiser les compétences par domaine - CALCULÉ DIRECTEMENT DEPUIS LES PROPS
    const skillsByDomain = useMemo(() => {
        const formatted = {};

        // Grouper les compétences par domaine
        skillsData.forEach(skill => {
            const domainId = skill.id_domaine;
            if (!formatted[domainId]) {
                formatted[domainId] = [];
            }
            formatted[domainId].push(skill);
        });

        return formatted;
    }, [skillsData, refreshTrigger]); // Ajouter refreshTrigger pour forcer le recalcul

    // Calculer le score réel pour un domaine basé sur ses compétences évaluées
    const calculateDomainScore = (domainId) => {
        const domainSkills = skillsByDomain[domainId] || [];

        if (domainSkills.length === 0) {
            return 0;
        }

        const totalRatingSum = domainSkills.reduce((sum, skill) => {
            return sum + (skill.rating || 0);
        }, 0);

        const maxPossibleScore = domainSkills.length * 5;
        const score = maxPossibleScore > 0 ? Math.round((totalRatingSum / maxPossibleScore) * 100) : 0;

        return score;
    };

    // Calculer les KPIs pour chaque domaine
    const getDomainKPIs = () => {
        return allDomains.map(domain => {
            const domainSkills = skillsByDomain[domain.id] || [];
            const skillsWithRating = domainSkills.filter(skill => skill.rating && skill.rating > 0);
            const completedInDomain = skillsWithRating.length;

            const progressScore = domainSkills.length > 0
                ? Math.round((completedInDomain / domainSkills.length) * 100)
                : 0;

            let masteryScore = 0;
            if (skillsWithRating.length > 0) {
                const avgRating = skillsWithRating.reduce((sum, skill) => sum + skill.rating, 0) / skillsWithRating.length;
                masteryScore = Math.round((avgRating / 5) * 100);
            }

            const calculatedScore = calculateDomainScore(domain.id);
            let overallScore = calculatedScore;
            const avgLevel = overallScore / 20;

            return {
                ...domain,
                avgLevel,
                completedInDomain,
                totalSkills: domainSkills.length,
                progressScore,
                masteryScore,
                overallScore,
                skillsWithRating: skillsWithRating.length,
                totalRating: domainSkills.reduce((sum, skill) => sum + (skill.rating || 0), 0),
                maxPossible: domainSkills.length * 5
            };
        });
    };

    const domainKPIs = getDomainKPIs();

    // Calculer les moyennes globales
    const avgProgressScore = domainKPIs.length > 0
        ? Math.round(domainKPIs.reduce((sum, d) => sum + d.progressScore, 0) / domainKPIs.length)
        : 0;
    const avgMasteryScore = domainKPIs.length > 0
        ? Math.round(domainKPIs.reduce((sum, d) => sum + d.masteryScore, 0) / domainKPIs.length)
        : 0;

    const hasAnyDomain = allDomains.length > 0;

    // Composant Treemap/Heatmap des domaines
    const DomainTreemap = () => {
        if (!hasAnyDomain) {
            return (
                <div className="flex items-center justify-center h-full">
                    <div className="text-center text-gray-500">
                        <Brain className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p className="text-lg font-medium mb-2">Aucun domaine d'expertise</p>
                        <p className="text-sm mb-4">
                            Créez vos premiers domaines pour visualiser votre cartographie
                        </p>
                        <div className="text-xs text-gray-400">
                            La cartographie vous permettra de voir vos forces et faiblesses en un coup d'œil
                        </div>
                    </div>
                </div>
            );
        }

        const treemapData = domainKPIs.map(domain => ({
            ...domain,
            value: domain.overallScore,
            area: Math.max(domain.overallScore, 5)
        }));

        treemapData.sort((a, b) => b.value - a.value);

        const calculateLayout = (data) => {
            const itemCount = data.length;
            const containerAspectRatio = 16 / 9;
            const cols = Math.ceil(Math.sqrt(itemCount * containerAspectRatio));
            const rows = Math.ceil(itemCount / cols);

            return data.map((item, index) => {
                const col = index % cols;
                const row = Math.floor(index / cols);
                const width = 100 / cols;
                const height = 100 / rows;

                return {
                    x: col * width,
                    y: row * height,
                    width: width - 0.2,
                    height: height - 0.2,
                    data: item
                };
            });
        };

        const layout = calculateLayout(treemapData);

        const getColor = (score) => {
            if (score === 0) return 'rgba(156, 163, 175, 0.85)';
            if (score > 60) return 'rgba(14, 148, 136, 0.85)';
            if (score > 40) return 'rgba(255, 173, 101, 0.85)';
            return 'rgba(229, 122, 134, 0.85)';
        };

        const getTextColor = (score) => {
            return '#FFFFFF'; // Toujours blanc
        };

        return (
            <div className="relative w-full h-full rounded-lg overflow-hidden">
                {layout.map((rect, index) => {
                    const domain = rect.data;
                    
                    // AJOUTÉ: Utiliser getDomainInfo pour les noms/icônes
                    const domainInfo = getDomainInfo(domain.name || domain.id);
                    const displayName = domainInfo?.nom || domain.name;
                    const displayIcon = domainInfo?.icon || domain.icon;
                    const IconComponent = displayIcon ? getReactIcon(displayIcon) : null;
                    
                    return (
                        <div
                            key={domain.id}
                            className="absolute border border-white transition-all hover:opacity-80 cursor-pointer flex flex-col justify-center items-center"
                            style={{
                                left: `${rect.x}%`,
                                top: `${rect.y}%`,
                                width: `${rect.width}%`,
                                height: `${rect.height}%`,
                                backgroundColor: getColor(domain.value),
                                color: getTextColor(domain.value)
                            }}
                            title={`${displayName}: ${domain.value}%${domain.value === 0 ? ' - Aucune évaluation' : ` - ${domain.totalRating}/${domain.maxPossible} points (${domain.skillsWithRating}/${domain.totalSkills} compétences évaluées)`}`}
                        >
                            <div className="text-center p-1 w-full h-full flex flex-col justify-center items-center">
                                {/* AJOUTÉ: Gestion des icônes React et emoji */}
                                <div className="flex items-center justify-center w-6 h-6 mb-1">
                                    {IconComponent ? (
                                        <IconComponent className="w-3 h-3 text-white" />
                                    ) : displayIcon ? (
                                        <span className="text-sm">
                                            {displayIcon}
                                        </span>
                                    ) : (
                                        <User className="w-3 h-3 text-white opacity-75" />
                                    )}
                                </div>
                                
                                {/* MODIFIÉ: Utiliser displayName */}
                                <div className="font-bold text-xs w-full px-1 text-white mb-1 text-center flex items-center justify-center">
                                    <span className="truncate max-w-full">
                                        {displayName}
                                    </span>
                                </div>
                                
                                <div className="text-xs opacity-90 font-semibold text-white mb-1">
                                    {domain.value}%
                                </div>
                                
                                <div className="text-xs opacity-75 text-white">
                                    {domain.value === 0 ? 'Non évalué' : `${domain.skillsWithRating}/${domain.totalSkills} éval.`}
                                </div>
                            </div>
                        </div>
                    );
                })}

                {/* Légende */}
                <div className="absolute bottom-2 right-2 bg-white bg-opacity-95 rounded px-2 py-1.5 text-xs shadow-sm border border-gray-200">
                    <div className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded" style={{ backgroundColor: 'rgba(229, 231, 235, 0.7)' }} title="0% - Non évalué"></div>
                        <div className="w-2 h-2 rounded" style={{ backgroundColor: 'rgba(229, 122, 134, 0.7)' }} title="1-34%"></div>
                        <div className="w-2 h-2 rounded" style={{ backgroundColor: 'rgba(255, 173, 101, 0.7)' }} title="35-49%"></div>
                        <div className="w-2 h-2 rounded" style={{ backgroundColor: 'rgba(24, 87, 79, 0.7)' }} title="50-64%"></div>
                        <div className="w-2 h-2 rounded" style={{ backgroundColor: 'rgba(1, 102, 102, 0.7)' }} title="65-79%"></div>
                        <div className="w-2 h-2 rounded" style={{ backgroundColor: 'rgba(14, 148, 136, 0.7)' }} title="≥ 80%"></div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 h-full flex flex-col">
            {/* Header avec sélecteur de vue */}
            <div className="bg-gray-100 border-b border-gray-200 px-4 py-3 -m-4 mb-4 flex-shrink-0 rounded-t-xl">
                <div className="flex items-center justify-between">
                    <div className="flex items-center text-gray-700">
                        <h2 className="text-sm font-semibold">Cartographie des Expertises</h2>
                    </div>
                    <div className="flex items-center space-x-2">
                        {/* Sélecteur de vue */}
                        <div className="flex items-center bg-white rounded-lg border border-gray-200 p-1">
                            <button
                                onClick={() => setViewMode('heatmap')}
                                className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                                    viewMode === 'heatmap'
                                        ? 'bg-[#18574F] text-white'
                                        : 'text-gray-600 hover:bg-gray-100'
                                }`}
                                title="Vue cartographie"
                            >
                                <Grid className="w-3 h-3" />
                                <span>Heatmap</span>
                            </button>
                            <button
                                onClick={() => setViewMode('barchart')}
                                className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                                    viewMode === 'barchart'
                                        ? 'bg-[#18574F] text-white'
                                        : 'text-gray-600 hover:bg-gray-100'
                                }`}
                                title="Vue graphique en barres"
                            >
                                <BarChart3 className="w-3 h-3" />
                                <span>Barres</span>
                            </button>
                        </div>
                        
                        {domainesData.length > 0 && (
                            <span className="bg-gray-200 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
                                {domainesData.length} domaine{domainesData.length > 1 ? 's' : ''}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Métriques rapides */}
            {hasAnyDomain && (
                <div className="grid grid-cols-4 gap-3 mb-4 flex-shrink-0">
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <div className="text-lg font-bold text-[#18574F]">
                            {domainKPIs.length}
                        </div>
                        <div className="text-xs text-gray-600">Domaines</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <div className="text-lg font-bold text-[#18574F]">
                            {domainKPIs.filter(d => d.totalSkills > 0).length}
                        </div>
                        <div className="text-xs text-gray-600">Avec compétences</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <div className="text-lg font-bold text-[#18574F]">
                            {avgProgressScore}%
                        </div>
                        <div className="text-xs text-gray-600">Progression</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <div className="text-lg font-bold text-[#18574F]">
                            {avgMasteryScore}%
                        </div>
                        <div className="text-xs text-gray-600">Maîtrise</div>
                    </div>
                </div>
            )}

            {/* Contenu principal - Vue conditionnelle */}
            <div className="flex-1 min-h-0">
                {viewMode === 'heatmap' ? (
                    <DomainTreemap />
                ) : (
                    <DomainBarChart domainKPIs={domainKPIs} hasAnyDomain={hasAnyDomain} />
                )}
            </div>
        </div>
    );
};